import { usePage } from '@inertiajs/vue3'
import { usePreferredDark } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onScopeDispose, watchEffect } from 'vue'

import type {
  AppearanceSharedProps,
  ResolveIsDarkInput,
  UseAppearanceReturn
} from '@/domains/admin-panel/composables/use-appearance/useAppearance.types'

import { useAppearanceStore } from '@/domains/admin-panel/store/use-appearance-store/useAppearanceStore'

// The class PrimeVue's dark scheme and Tailwind's `dark:` both key on (app.ts, tailwind.config.js).
const DARK_CLASS = 'app-dark'

/** Twine's switch wins over everything: off renders light, whatever the user saved or the system prefers. */
export const resolveIsDark = ({ isDarkModeAvailable, appearance, prefersDark }: ResolveIsDarkInput): boolean => {
  if (!isDarkModeAvailable) return false
  if (appearance === 'system') return prefersDark

  return appearance === 'dark'
}

export const useAppearance = (): UseAppearanceReturn => {
  const page = usePage()
  const prefersDark = usePreferredDark()
  const appearanceStore = useAppearanceStore()
  const { appearance } = storeToRefs(appearanceStore)

  const isDarkModeAvailable = computed(
    () => (page.props.appearance as AppearanceSharedProps | undefined)?.is_dark_mode_available === true
  )
  const isDark = computed(() =>
    resolveIsDark({
      isDarkModeAvailable: isDarkModeAvailable.value,
      appearance: appearance.value,
      prefersDark: prefersDark.value
    })
  )

  return {
    isDarkModeAvailable,
    appearance,
    isDark,
    setAppearance: appearanceStore.setAppearance
  }
}

/** Keeps the dark class on <html> in step with the choice; the layout that owns the admin calls it once. */
export const useAppearanceClass = (): void => {
  const { isDark } = useAppearance()

  watchEffect(() => document.documentElement.classList.toggle(DARK_CLASS, isDark.value))
  // A layout that stops owning the page must not leave its dark class behind.
  onScopeDispose(() => document.documentElement.classList.remove(DARK_CLASS))
}
